import { useState } from "react";
import { useAppDispatch, useAppSelector } from "index";

import { LinearProgress, ListItem, ListItemButton, ListItemIcon, ListItemText, TextField } from "@mui/material";
import List from "@mui/material/List";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { ApplicationState } from "store/index";
import { ParsedExport } from "@store/data/types";
import { renameExport, switchActive } from "@store/data/actions";
import useTranslations from "containers/hooks/useLanguageHook";
import DataExportMenu from "./DataExportMenu";
import { fileSizeToString } from "@/utils/format";

function DataExportSelector() {
  const items = useAppSelector((state: ApplicationState) => state.data.exports);
  const files = useAppSelector((state: ApplicationState) => state.fileParsing.file);
  const dispatch = useAppDispatch();
  const { t } = useTranslations();

  const progress = files.reduce((max, file) => Math.max(max, file.progress), 0);

  const [renaming, setRenaming] = useState<boolean>();

  return (
    <List dense component="div" disablePadding>
      {items.length === 0 ? (
        <Box>
          <Typography sx={{ p: 1 }}>{t.Vis.DataExportsNonLoaded}</Typography>
        </Box>
      ) : (
        items.map((d) => renderLoadedFile(d))
      )}
      {files.length > 0 && renderLoadingFile(progress)}
    </List>
  );

  function renderLoadedFile(item: ParsedExport) {
    return (
      <ListItem key={item.id} sx={{ m: 0, p: 0 }}>
        {renaming ? (
          <Box
            sx={{
              padding: 1,
            }}
          >
            <TextField
              label={t.Options.SelectorItemOptions.provider}
              defaultValue={item.properties.providerName}
              variant="outlined"
              size="small"
              onKeyDown={(e) => {
                if (e.key.toLowerCase() === "enter") {
                  //eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  dispatch(renameExport(item.id, e.target.value));
                  setRenaming(false);
                }
              }}
              // TODO: shouldnt this also have a clearImmediate?
              inputRef={(input) => input && setImmediate(() => input.focus())}
            />
          </Box>
        ) : (
          <ListItemButton
            role="checkbox"
            onClick={() => dispatch(switchActive(item.id))}
            sx={{
              gap: 1,
              paddingLeft: 1,
              paddingRight: 0,
            }}
          >
            <ListItemIcon sx={{ width: 20, minWidth: 20 }}>
              <Checkbox edge="start" checked={item.active} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Tooltip
                  placement="right"
                  title={
                    <Box>
                      <Typography>
                        <b>{t.Options.SelectorItemOptions.provider}</b>:{" "}
                        {item.properties.originalProviderName ?? "Unknown"}
                      </Typography>
                      <Typography>
                        <b>{t.Vis.ExportFullName}</b>: {item.fileProperties.fileName}
                      </Typography>
                      <Typography>
                        <b>{t.Vis.ExportNumberOfFiles}</b>: {item.children.length}
                      </Typography>
                      <Typography>
                        <b>{t.Vis.ExportFileSize}</b>: {fileSizeToString(item.fileProperties.fileSize)}
                      </Typography>
                      <Typography>{!item.active ? t.Vis.ClickToShowExport : t.Vis.ClickToHideExport}</Typography>
                    </Box>
                  }
                >
                  <Box>
                    <Typography
                      variant="h6"
                      align="left"
                      sx={{
                        overflow: "hidden",
                        overflowWrap: "anywhere",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.providerId === "wrangled"
                        ? t.DataPage.UnknownProvider
                        : item.properties.providerName || t.DataPage.UnknownProvider}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      align="left"
                      sx={{
                        overflow: "hidden",
                        overflowWrap: "anywhere",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.fileProperties.fileName}
                    </Typography>
                  </Box>
                </Tooltip>
              }
            />
          </ListItemButton>
        )}
        <DataExportMenu
          selectedExport={item}
          onRename={() => {
            setRenaming(true);
          }}
        />
      </ListItem>
    );
  }

  function renderLoadingFile(progress: number) {
    return (
      <ListItem>
        <ListItemText primary={<LinearProgress sx={{ height: 24 }} value={progress} />} />
      </ListItem>
    );
  }
}

export default DataExportSelector;
